/**
 * buildIslandsAtlas
 * ------------------------------------------------------------
 * The Dutch Wadden islands and Texel are NOT separate regions in
 * provinces.topo.json — they live inside the MultiPolygon geometries
 * of NL-NH (Texel) and NL-FR (Vlieland, Terschelling, Ameland,
 * Schiermonnikoog) as small "outlying" sub-polygons.
 *
 * This module reuses the EXACT PDOK geometry already shipped with the
 * provinces atlas to build a synthetic `nl-islands` TopoJSON at runtime.
 * No extra data, no external fetch — just re-slicing what we already have.
 *
 * Output: a topojson topology with:
 *   - 5 clickable island regions (NL-TEXEL, NL-VLIELAND, NL-TERSCHELLING,
 *     NL-AMELAND, NL-SCHIERMONNIKOOG)
 *   - 3 reference mainland regions (NL-NH-MAIN, NL-FR-MAIN, NL-GR-MAIN)
 *     with property `isReference: true` so the renderer can disable clicks.
 */

// @ts-ignore
import type { Topology } from 'topojson-specification'
import * as topojson from 'topojson-client'
import provincesTopo from '../../../content/atlases/nl/provinces.topo.json'

type Coord = [number, number]
type Ring = Coord[]
type PolygonCoords = Ring[] // [outerRing, ...holes]
type MultiPolygonCoords = PolygonCoords[]

interface IslandReference {
  id: string
  name: string
}

/** Texel is the only Wadden island that lives inside NL-NH. */
const NH_ISLAND: IslandReference = {
  id: 'NL-TEXEL',
  name: 'Texel',
}

/**
 * The four Frisian Wadden islands, ordered strictly west → east.
 * After we pick the 4 largest non-mainland sub-polygons of NL-FR
 * and sort them by centroid longitude, index 0 = Vlieland, etc.
 */
const FR_ISLANDS_WEST_TO_EAST: IslandReference[] = [
  { id: 'NL-VLIELAND', name: 'Vlieland' },
  { id: 'NL-TERSCHELLING', name: 'Terschelling' },
  { id: 'NL-AMELAND', name: 'Ameland' },
  { id: 'NL-SCHIERMONNIKOOG', name: 'Schiermonnikoog' },
]

/** All clickable islands (used for the name map). */
const ALL_ISLANDS: IslandReference[] = [NH_ISLAND, ...FR_ISLANDS_WEST_TO_EAST]

// Mainland reference regions — picked as the LARGEST sub-polygon of each
// host province, used for visual context (Noord-Holland tip, Friesland
// north coast, Groningen north coast). Marked non-interactive in the UI.
const MAINLAND_HOSTS: Array<{ id: string; name: string; provinceId: string }> =
  [
    { id: 'NL-NH-MAIN', name: 'Noord-Holland', provinceId: 'NL-NH' },
    { id: 'NL-FR-MAIN', name: 'Friesland', provinceId: 'NL-FR' },
    { id: 'NL-GR-MAIN', name: 'Groningen', provinceId: 'NL-GR' },
  ]

// ── Geometry helpers ────────────────────────────────────────

function ringCentroid(ring: Ring): Coord {
  // Simple area-weighted centroid for a closed polygon ring (lon/lat).
  // Adequate for selecting which sub-polygon corresponds to which island.
  let twiceArea = 0
  let cx = 0
  let cy = 0
  const n = ring.length
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = ring[i]
    const [x1, y1] = ring[i + 1]
    const cross = x0 * y1 - x1 * y0
    twiceArea += cross
    cx += (x0 + x1) * cross
    cy += (y0 + y1) * cross
  }
  if (Math.abs(twiceArea) < 1e-12) {
    // Degenerate — fall back to bbox center
    let sx = 0
    let sy = 0
    for (const [x, y] of ring) {
      sx += x
      sy += y
    }
    return [sx / ring.length, sy / ring.length]
  }
  const area = twiceArea / 2
  return [cx / (6 * area), cy / (6 * area)]
}

function ringAreaAbs(ring: Ring): number {
  let twiceArea = 0
  const n = ring.length
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = ring[i]
    const [x1, y1] = ring[i + 1]
    twiceArea += x0 * y1 - x1 * y0
  }
  return Math.abs(twiceArea / 2)
}

// ── Island matcher ──────────────────────────────────────────

interface MatchedRing {
  ring: Ring
  centroid: Coord
  area: number
}

/**
 * Given the MultiPolygon coordinates of a host province, return its
 * sub-polygons as MatchedRing[] (outer ring only, with centroid + area).
 */
function extractSubPolygons(coords: MultiPolygonCoords): MatchedRing[] {
  return coords.map((poly) => {
    const outer = poly[0]
    return {
      ring: outer,
      centroid: ringCentroid(outer),
      area: ringAreaAbs(outer),
    }
  })
}

/**
 * Pick the N largest sub-polygons (by area) and sort them west → east
 * (by centroid longitude). This is more robust than nearest-centroid
 * matching: inhabited Wadden islands are visibly larger than the sandbars
 * and uninhabited islets that share the same MultiPolygon, so "largest N"
 * cleanly isolates them, and their geographic order west→east is fixed.
 */
function pickLargestSortedWestEast(
  rings: MatchedRing[],
  count: number,
): MatchedRing[] {
  return [...rings]
    .sort((a, b) => b.area - a.area)
    .slice(0, count)
    .sort((a, b) => a.centroid[0] - b.centroid[0])
}

// ── Public builder ──────────────────────────────────────────

interface RegionGeometryEntry {
  id: string
  type: 'Polygon'
  arcs: number[][]
  properties: { name: string; isReference?: boolean }
}

let cachedIslandsTopo: Topology | null = null

export function getIslandsTopology(): Topology {
  if (cachedIslandsTopo) return cachedIslandsTopo

  // 1. Decode provinces TopoJSON to GeoJSON FeatureCollection
  const provincesGeo = topojson.feature(
    provincesTopo as unknown as Topology,
    // @ts-ignore - object key exists at runtime
    provincesTopo.objects.regions,
  ) as unknown as {
    features: Array<{
      id: string
      geometry: {
        type: 'Polygon' | 'MultiPolygon'
        coordinates: PolygonCoords | MultiPolygonCoords
      }
    }>
  }

  // 2. Build per-province sub-polygon catalog
  const subPolyByProvince = new Map<string, MatchedRing[]>()
  for (const f of provincesGeo.features) {
    if (f.geometry.type === 'MultiPolygon') {
      subPolyByProvince.set(
        f.id,
        extractSubPolygons(f.geometry.coordinates as MultiPolygonCoords),
      )
    } else if (f.geometry.type === 'Polygon') {
      const ring = (f.geometry.coordinates as PolygonCoords)[0]
      subPolyByProvince.set(f.id, [
        { ring, centroid: ringCentroid(ring), area: ringAreaAbs(ring) },
      ])
    }
  }

  // 3. Identify mainland rings (largest sub-polygon per host province)
  //    and the island candidates (everything else).
  const arcs: Coord[][] = []
  const geometries: RegionGeometryEntry[] = []
  const mainlandRings = new Map<string, MatchedRing>()
  const nonMainlandByProvince = new Map<string, MatchedRing[]>()

  for (const m of MAINLAND_HOSTS) {
    const subs = subPolyByProvince.get(m.provinceId) ?? []
    if (subs.length === 0) continue
    let largest = subs[0]
    for (const s of subs) if (s.area > largest.area) largest = s
    mainlandRings.set(m.provinceId, largest)
    nonMainlandByProvince.set(
      m.provinceId,
      subs.filter((s) => s !== largest),
    )
  }

  // 4a. Texel = largest non-mainland sub-polygon of NL-NH.
  const nhCandidates = nonMainlandByProvince.get('NL-NH') ?? []
  const texelMatch = pickLargestSortedWestEast(nhCandidates, 1)[0]
  if (texelMatch) {
    const arcIndex = arcs.length
    arcs.push(texelMatch.ring)
    geometries.push({
      id: NH_ISLAND.id,
      type: 'Polygon',
      arcs: [[arcIndex]],
      properties: { name: NH_ISLAND.name },
    })
  }

  // 4b. Vlieland/Terschelling/Ameland/Schiermonnikoog = 4 largest
  //     non-mainland sub-polygons of NL-FR, sorted west→east.
  //     This cleanly excludes any small uninhabited sandbars (Griend etc.).
  const frCandidates = nonMainlandByProvince.get('NL-FR') ?? []
  const frMatches = pickLargestSortedWestEast(
    frCandidates,
    FR_ISLANDS_WEST_TO_EAST.length,
  )
  for (let i = 0; i < FR_ISLANDS_WEST_TO_EAST.length; i++) {
    const ring = frMatches[i]
    if (!ring) continue
    const island = FR_ISLANDS_WEST_TO_EAST[i]
    const arcIndex = arcs.length
    arcs.push(ring.ring)
    geometries.push({
      id: island.id,
      type: 'Polygon',
      arcs: [[arcIndex]],
      properties: { name: island.name },
    })
  }

  // 5. Add mainland reference regions (non-interactive)
  for (const m of MAINLAND_HOSTS) {
    const ring = mainlandRings.get(m.provinceId)
    if (!ring) continue
    const arcIndex = arcs.length
    arcs.push(ring.ring)
    geometries.push({
      id: m.id,
      type: 'Polygon',
      arcs: [[arcIndex]],
      properties: { name: m.name, isReference: true },
    })
  }

  cachedIslandsTopo = {
    type: 'Topology',
    // No transform → arcs are absolute [lon, lat] coordinates
    arcs: arcs as unknown as number[][][],
    objects: {
      regions: {
        type: 'GeometryCollection',
        geometries: geometries as unknown as Array<Record<string, unknown>>,
      } as unknown as Record<string, unknown>,
    },
  } as unknown as Topology

  return cachedIslandsTopo
}

/**
 * Names of the non-interactive (reference) regions in the islands atlas.
 * Used by renderers to disable clicks on the mainland.
 */
export const ISLANDS_REFERENCE_REGION_IDS = MAINLAND_HOSTS.map((m) => m.id)

/**
 * Map of island region IDs → Dutch display names.
 * Used by the name resolver (no longer needs to import an islands JSON).
 */
export const ISLANDS_NAME_BY_ID: Record<string, string> = (() => {
  const out: Record<string, string> = {}
  for (const i of ALL_ISLANDS) out[i.id] = i.name
  for (const m of MAINLAND_HOSTS) out[m.id] = m.name
  return out
})()
